import { useState, useRef, useCallback, useEffect } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'
import {
  Upload,
  Film,
  Scissors,
  Play,
  Download,
  AlertCircle,
  Loader2,
  Monitor,
  Gauge,
  Image,
  Trash2,
  Wand2,
  Settings
} from 'lucide-react'

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 10)
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`
}

function App() {
  const { t } = useI18n()
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [videoUrl, setVideoUrl] = useState('')
  const [videoInfo, setVideoInfo] = useState({ duration: 0, width: 0, height: 0, size: 0 })
  const [startTime, setStartTime] = useState(0)
  const [endTime, setEndTime] = useState(5)
  const [fps, setFps] = useState(10)
  const [outputWidth, setOutputWidth] = useState(480)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isLoadingFfmpeg, setIsLoadingFfmpeg] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressMsg, setProgressMsg] = useState('')
  const [gifUrl, setGifUrl] = useState('')
  const [gifSize, setGifSize] = useState(0)
  const [error, setError] = useState('')
  const [isPlaying, setIsPlaying] = useState(false)
  const ffmpegRef = useRef<FFmpeg | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Load ffmpeg.wasm on first use
  const loadFfmpeg = useCallback(async () => {
    if (ffmpegRef.current || isLoadingFfmpeg) return
    setIsLoadingFfmpeg(true)
    setProgressMsg(t('videoGif.loadingEngine') || 'Loading video processing engine...')
    try {
      const ffmpeg = new FFmpeg()
      ffmpeg.on('progress', ({ progress: p }) => {
        setProgress(Math.round(p * 100))
      })
      ffmpeg.on('log', ({ message }) => {
        if (message.includes('frame=') || message.includes('size=')) {
          setProgressMsg(message)
        }
      })
      await ffmpeg.load()
      ffmpegRef.current = ffmpeg
    } catch (e) {
      setError(t('videoGif.ffmpegError') || 'Failed to load video processing engine. Please refresh and try again.')
    } finally {
      setIsLoadingFfmpeg(false)
      setProgressMsg('')
    }
  }, [isLoadingFfmpeg, t])

  // Handle file upload
  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('video/')) {
      setError(t('videoGif.invalidFile') || 'Please upload a video file (MP4, WEBM, MOV)')
      return
    }
    if (file.size > 500 * 1024 * 1024) {
      setError(t('videoGif.fileTooLarge') || 'File too large. Maximum 500MB.')
      return
    }

    // Cleanup previous
    if (videoUrl) URL.revokeObjectURL(videoUrl)
    if (gifUrl) URL.revokeObjectURL(gifUrl)

    const url = URL.createObjectURL(file)
    setVideoFile(file)
    setVideoUrl(url)
    setGifUrl('')
    setGifSize(0)
    setError('')
    setProgress(0)

    // Get video metadata
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      const duration = video.duration || 0
      setVideoInfo({
        duration,
        width: video.videoWidth,
        height: video.videoHeight,
        size: file.size,
      })
      setStartTime(0)
      setEndTime(Math.min(duration, 5))
      setOutputWidth(Math.min(video.videoWidth, 480))
    }
    video.onerror = () => {
      setError(t('videoGif.loadError') || 'Failed to load video. The format may not be supported.')
    }
    video.src = url
  }, [videoUrl, gifUrl, t])

  // Drag and drop handlers
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  // File input handler
  const onFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }, [handleFile])

  // Play/pause preview
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return
    if (isPlaying) {
      videoRef.current.pause()
    } else {
      videoRef.current.currentTime = startTime
      videoRef.current.play()
      // Auto pause at end time
      const checkTime = () => {
        if (!videoRef.current) return
        if (videoRef.current.currentTime >= endTime) {
          videoRef.current.pause()
          setIsPlaying(false)
        } else if (!videoRef.current.paused) {
          requestAnimationFrame(checkTime)
        }
      }
      requestAnimationFrame(checkTime)
    }
    setIsPlaying(!isPlaying)
  }, [isPlaying, startTime, endTime])

  // Generate GIF
  const generateGif = useCallback(async () => {
    if (!videoFile) return

    // Load ffmpeg if not ready
    if (!ffmpegRef.current) {
      await loadFfmpeg()
      if (!ffmpegRef.current) return
    }

    setIsGenerating(true)
    setProgress(0)
    setError('')
    setGifUrl('')

    try {
      const ffmpeg = ffmpegRef.current
      const duration = Math.min(endTime - startTime, videoInfo.duration - startTime)

      if (duration <= 0) {
        setError(t('videoGif.invalidRange') || 'Invalid time range')
        setIsGenerating(false)
        return
      }

      setProgressMsg(t('videoGif.processing') || 'Processing video...')

      // Write input file
      await ffmpeg.writeFile('input', await fetchFile(videoFile))

      // Build ffmpeg args
      const scaleFilter = outputWidth > 0
        ? `scale=${outputWidth}:-1:flags=lanczos`
        : 'scale=480:-1:flags=lanczos'

      await ffmpeg.exec([
        '-i', 'input',
        '-ss', String(startTime),
        '-t', String(duration),
        '-vf', `fps=${fps},${scaleFilter},split[s0][s1];[s0]palettegen=max_colors=128[p];[s1][p]paletteuse=dither=bayer`,
        '-loop', '0',
        '-y',
        'output.gif',
      ])

      // Read output
      const data = await ffmpeg.readFile('output.gif') as Uint8Array
      const blob = new Blob([new Uint8Array(data)], { type: 'image/gif' })
      const url = URL.createObjectURL(blob)
      setGifUrl(url)
      setGifSize(blob.size)

      // Cleanup ffmpeg files
      try { await ffmpeg.deleteFile('input') } catch { /* ignore */ }
      try { await ffmpeg.deleteFile('output.gif') } catch { /* ignore */ }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setError((t('videoGif.generateError') || 'Failed to generate GIF: ') + msg)
    } finally {
      setIsGenerating(false)
      setProgress(0)
      setProgressMsg('')
    }
  }, [videoFile, startTime, endTime, fps, outputWidth, videoInfo.duration, loadFfmpeg, t])

  // Clear all
  const clearAll = useCallback(() => {
    if (videoUrl) URL.revokeObjectURL(videoUrl)
    if (gifUrl) URL.revokeObjectURL(gifUrl)
    setVideoFile(null)
    setVideoUrl('')
    setVideoInfo({ duration: 0, width: 0, height: 0, size: 0 })
    setStartTime(0)
    setEndTime(5)
    setFps(10)
    setOutputWidth(480)
    setGifUrl('')
    setGifSize(0)
    setError('')
    setProgress(0)
    setIsPlaying(false)
  }, [videoUrl, gifUrl])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl)
      if (gifUrl) URL.revokeObjectURL(gifUrl)
    }
  }, [])

  const fileSizeMB = videoInfo.size > 0 ? (videoInfo.size / 1024 / 1024).toFixed(1) : '0'

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('videoGif.title') || 'Video to GIF Converter'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('videoGif.subtitle') || 'Convert MP4, WEBM, MOV videos to animated GIFs online.'}
          <span className="text-primary font-medium"> {t('videoGif.privacy') || '100% client-side'}</span>, {t('videoGif.noUpload') || 'no upload needed'}.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Upload Area */}
        {!videoFile ? (
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="bg-surface rounded-xl shadow-sm border border-border p-8 md:p-12 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Upload className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('videoGif.uploadTitle') || 'Upload Video'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('videoGif.uploadDesc') || 'Drag & drop or click to select. Supports MP4, WEBM, MOV up to 500MB.'}
            </p>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium cursor-pointer">
              <Film className="w-4 h-4" />
              {t('videoGif.selectFile') || 'Select Video'}
              <input type="file" accept="video/*" className="hidden" onChange={onFileInput} />
            </label>
          </div>
        ) : (
          <>
            {/* Video Info Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-surface rounded-xl shadow-sm border border-border p-4">
              <div className="flex items-center gap-3">
                <Film className="w-5 h-5 text-brand-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">{videoFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {fileSizeMB} MB · {videoInfo.width}x{videoInfo.height} · {formatTime(videoInfo.duration)}
                  </p>
                </div>
              </div>
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('actions.clear') || 'Clear'}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: Video Preview */}
              <div className="bg-surface rounded-xl shadow-sm border border-border p-4 space-y-4">
                <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    className="w-full h-full object-contain"
                    controls={!isPlaying}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                </div>

                {/* Time Range */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Scissors className="w-4 h-4" />
                    {t('videoGif.timeRange') || 'Time Range'}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {formatTime(endTime - startTime)} selected
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-12 shrink-0">{t('videoGif.start') || 'Start'}</span>
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, videoInfo.duration - 0.5)}
                        step={0.1}
                        value={startTime}
                        onChange={e => {
                          const val = Number(e.target.value)
                          setStartTime(Math.min(val, endTime - 0.5))
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs text-muted-foreground w-16 text-right">{formatTime(startTime)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-12 shrink-0">{t('videoGif.end') || 'End'}</span>
                      <input
                        type="range"
                        min={0.5}
                        max={videoInfo.duration}
                        step={0.1}
                        value={endTime}
                        onChange={e => {
                          const val = Number(e.target.value)
                          setEndTime(Math.max(val, startTime + 0.5))
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs text-muted-foreground w-16 text-right">{formatTime(endTime)}</span>
                    </div>
                  </div>

                  <button
                    onClick={togglePlay}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium text-foreground"
                  >
                    <Play className="w-4 h-4" />
                    {isPlaying ? (t('videoGif.pause') || 'Pause') : (t('videoGif.play') || 'Play Selection')}
                  </button>
                </div>
              </div>

              {/* Right: Settings */}
              <div className="bg-surface rounded-xl shadow-sm border border-border p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Settings className="w-4 h-4" />
                  {t('videoGif.settings') || 'Output Settings'}
                </div>

                {/* FPS */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm text-foreground flex items-center gap-1.5">
                      <Gauge className="w-3.5 h-3.5" />
                      {t('videoGif.fps') || 'FPS'}
                    </label>
                    <span className="text-xs text-muted-foreground">{fps}</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={fps}
                    onChange={e => setFps(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('videoGif.fpsDesc') || 'Higher FPS = smoother but larger file'}
                  </p>
                </div>

                {/* Width */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm text-foreground flex items-center gap-1.5">
                      <Monitor className="w-3.5 h-3.5" />
                      {t('videoGif.width') || 'Width'}
                    </label>
                    <span className="text-xs text-muted-foreground">{outputWidth}px</span>
                  </div>
                  <input
                    type="range"
                    min={100}
                    max={800}
                    step={10}
                    value={outputWidth}
                    onChange={e => setOutputWidth(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('videoGif.widthDesc') || 'Height scales automatically'}
                  </p>
                </div>

                {/* Info */}
                <div className="bg-muted rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                  <p>{t('videoGif.estimated') || 'Estimated'}: {Math.ceil((endTime - startTime) * fps)} {t('videoGif.frames') || 'frames'}</p>
                  <p>{t('videoGif.originalSize') || 'Original'}: {videoInfo.width}x{videoInfo.height}</p>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateGif}
                  disabled={isGenerating || isLoadingFfmpeg}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
                >
                  {isLoadingFfmpeg ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('videoGif.loading') || 'Loading Engine...'}
                    </>
                  ) : isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('videoGif.generating') || 'Generating...'} {progress}%
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      {t('videoGif.generate') || 'Generate GIF'}
                    </>
                  )}
                </button>

                {/* Progress */}
                {(isGenerating || isLoadingFfmpeg) && (
                  <div className="space-y-1">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-primary rounded-full transition-all duration-300"
                        style={{ width: `${isLoadingFfmpeg ? Math.min(progress, 30) : progress}%` }}
                      />
                    </div>
                    {progressMsg && (
                      <p className="text-xs text-muted-foreground font-mono truncate">{progressMsg}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* GIF Output */}
            {gifUrl && (
              <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-4">
                  <Image className="w-4 h-4" />
                  {t('videoGif.result') || 'Result'}
                  {gifSize > 0 && (
                    <span className="text-xs text-muted-foreground ml-auto">
                      {(gifSize / 1024).toFixed(0)} KB
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-center bg-black rounded-lg p-4 min-h-[200px]">
                  <img
                    src={gifUrl}
                    alt="Generated GIF"
                    className="max-w-full max-h-[400px]"
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <a
                    href={gifUrl}
                    download={`${videoFile?.name.replace(/\.[^/.]+$/, '') || 'output'}.gif`}
                    className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    {t('actions.download') || 'Download GIF'}
                  </a>
                </div>
              </div>
            )}
          </>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
